export type SamplePrompt = {
  n: number;
  title: string;
  purpose: string;
  text: string;
  tips: string[];
  expectedOutput: string;
  proTip: string;
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
];

export const howSteps = [
  {
    title: 'You provide the project information',
    desc: 'Add the relevant client brief, service description, project objectives, requirements, and constraints.',
    chips: ['[CLIENT BRIEF]', '[YOUR SERVICE]', '[KNOWN PROJECT REQUIREMENTS]', '[KNOWN CONSTRAINTS OR DEADLINES]'],
  },
  {
    title: 'The prompt guides the AI',
    desc: 'The structured instructions help organize the information and request a proposal draft with clearly defined sections.',
    chips: ['ChatGPT', 'Claude', 'Gemini'],
  },
  {
    title: 'You review and customize the result',
    desc: "Check the output against the client's actual requirements. Correct inaccuracies, clarify missing details, and confirm that the scope and commitments reflect what you are prepared to deliver.",
    chips: [] as string[],
  },
];

export const safeguards = [
  'Distinguish confirmed requirements from recommendations',
  'List missing information and questions for the client',
  'Flag assumptions and dependencies to confirm',
  'Avoid invented prices, deadlines, or credentials',
  'Keep a clear, professional structure',
  'Review and approve before sending',
];

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Client Brief to Proposal Outline',
    purpose: "Organize a client's initial information into a structured proposal outline before drafting the full document.",
    text: `Act as an experienced freelance proposal strategist. Analyze the client information below and create a structured proposal outline based only on the information supplied.

Client brief: [CLIENT BRIEF]
My service: [YOUR SERVICE]
Known project requirements: [KNOWN PROJECT REQUIREMENTS]
Known constraints or deadlines: [KNOWN CONSTRAINTS OR DEADLINES]

Organize the output into these sections:
1. Client objectives
2. Confirmed project requirements
3. Preliminary scope and proposed deliverables
4. Missing information and questions for the client
5. Assumptions and dependencies to confirm
6. Recommended proposal outline
7. Suggested next steps before the proposal is finalized

Distinguish confirmed requirements from recommendations. Do not invent client requirements, prices, deadlines, credentials, or expected results. If important information is missing, list the questions that should be answered before the proposal is finalized.

Use clear, professional language and organize the response with descriptive headings and concise bullet points.`,
    tips: [
      "The client's objectives and requirements are represented accurately.",
      'Deliverables and exclusions are clear.',
      'Deadlines and dependencies are realistic and confirmed.',
      'Prices, payment terms, and commercial details are accurate.',
      'Revision limits and responsibilities are defined where relevant.',
      'AI-generated claims and details have been verified.',
      'Confidential client information is handled appropriately.',
      'The proposal has been reviewed and approved by you before sending.',
    ],
    expectedOutput:
      'A structured project summary, preliminary scope, list of missing information, assumptions to confirm, and proposal outline ready for review.',
    proTip:
      'Use the outline as a preparation tool—not as an automatic commitment to the client. Confirm unclear requirements and verify the proposed deliverables before sending a final proposal.',
  },
];
